import {
  Suspense,
  useEffect,
  useMemo,
  useState,
  useTransition,
  type ReactNode,
} from "react";

import {
  ArrowRightIcon,
  Button,
  CaretRightIcon,
  CheckIcon,
  PencilSimpleIcon,
  ProgressBar,
  Chip,
  Tooltip,
  VisuallyHidden,
  XIcon,
  cn,
  focusableTriggerClasses,
  formatProvider,
  isProvider,
} from "@stacklet/ui";
import pluralize from "pluralize";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";

import { Avatar } from "app/components/avatar/Avatar";
import { ExportCsv } from "app/components/csv-export/ExportCsv";
import { useFilteredCsvExport } from "app/components/csv-export/useFilteredCsvExport";
import { HumanTimestamp } from "app/components/HumanTimestamp";
import { Link, TableLink } from "app/components/links";
import { IdentityProviderLogo } from "app/components/rbac/IdentityProviderLogo";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  PaginationFooter,
  StackletGrid,
  type StackletGridPagination,
} from "app/components/table/StackletGrid";
import { SEARCH_DEBOUNCE_MS } from "app/constants";
import { useUserContext } from "app/contexts/UserContext";
import {
  firstPage,
  useDebouncedCallback,
  useSectionPagination,
  useStickyPageSize,
  type SectionPage,
} from "app/hooks/ui";

import { buildFilter } from "./UserBrowser";
import { groupingDetailPath, type RetainedSearch } from "./userGrouping";

import type {
  GroupedUsersGridMembersQuery,
  GroupedUsersGridMembersQuery$data,
} from "./__generated__/GroupedUsersGridMembersQuery.graphql";
import type { GroupedUsersGridQuery } from "./__generated__/GroupedUsersGridQuery.graphql";
import type {
  GroupedUsersGridSearchFragment$data,
  GroupedUsersGridSearchFragment$key,
} from "./__generated__/GroupedUsersGridSearchFragment.graphql";
import type { GroupedUsersGridSearchRefetchQuery } from "./__generated__/GroupedUsersGridSearchRefetchQuery.graphql";
import type { UserBrowserScope } from "./UserBrowser";
import type { GridColDef } from "@mui/x-data-grid-pro";

/** Columns included in the CSV export of the directory's users. */
const CSV_COLUMNS = [
  { name: "Name", path: "name" },
  { name: "Email", path: "email" },
  { name: "Last Login", path: "lastLogin" },
] as const;

/** Matching users a single user-first search fetches before capping. */
const SEARCH_LIMIT = 200;
/** Group/role chips shown on a user row before the rest collapse to "+N more". */
const MAX_GROUP_CHIPS = 4;
/** Content columns (everything after the disclosure toggle); band and pager
 * rows span them all. */
const COLUMN_COUNT = 5;
/** Fixed columns exempt from autosize, so it can't widen them. Stable identity. */
const FIXED_WIDTH_FIELDS = ["disclosure", "hasApiKeys", "lastLogin", "actions"];

const GRID_CLASS_NAMES = [
  "[&_.MuiDataGrid-cell]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!px-xs",
  "[&_.MuiDataGrid-columnHeader]:!text-text-secondary",
  "[&_.MuiDataGrid-columnHeaderTitle]:!text-label-small",
  // The inner column dividers are a lighter 1px than the header's bottom border.
  "[&_.MuiDataGrid-cell]:!border-r-overlay-medium",
  "[&_.MuiDataGrid-columnHeader]:!border-r-overlay-medium",
].join(" ");

/** One bucket (group / role / provider) to list as an expandable section. */
export interface BucketRow {
  /** Stable id. */
  id: string;
  /** Section band label. */
  label: string;
  /** Details-page link for the band, or null when there is none. */
  to?: string | null;
  /** Edit-page link for the band's ✎ action, or null to omit it. */
  editTo?: string | null;
  /** Selects this bucket's members. */
  memberScope: UserBrowserScope;
  /** Short secondary label after the band name (a group's source, say). */
  detail?: string | null;
  /** When grouping by group, the section's group uuid — its chip is filled on each row. */
  highlightGroupUuid?: string;
}

/** A group a member belongs to, for the row's chips. */
interface MemberGroup {
  uuid: string;
  name: string;
  displayName?: string | null;
  /** The type of provider the group was synced from, for its logo; null for a
   * local group. */
  idpType: string | null;
  /** The provider the group was synced from, for disambiguating same-named
   * groups across providers; null for a local group. */
  idpLabel: string | null;
}

/** The `saml::` prefix a SAML provider's default namespace carries; dropped
 * from the label since the provider name alone reads better. */
const SAML_NAMESPACE_PREFIX = "saml::";

/**
 * A synced group's provider label: the provider's alias, else its namespace
 * minus any `saml::` prefix (`saml::okta` → `okta`). A bare cloud provider key
 * (`aws`, `gcp`) is display-cased (`AWS`, `GCP`).
 */
function idpLabelFromSource(source: {
  idpAlias?: string | null;
  idpNamespace: string;
}): string {
  const label =
    source.idpAlias ||
    (source.idpNamespace.startsWith(SAML_NAMESPACE_PREFIX)
      ? source.idpNamespace.slice(SAML_NAMESPACE_PREFIX.length)
      : source.idpNamespace);
  return isProvider(label) ? formatProvider(label) : label;
}

/** A member group's plain-text label: its name, plus the provider in parens
 * when it was synced, since groups from different providers can share a name.
 * Used where the chip's logo + tooltip can't be (inside another tooltip). */
function memberGroupLabel(group: MemberGroup): string {
  const name = group.displayName || group.name;
  return group.idpLabel ? `${name} (${group.idpLabel})` : name;
}

interface MemberGroupChipProps {
  /** The group to show. */
  group: MemberGroup;
  /** Whether this is the section's own group, which gets the filled colour. */
  isHighlighted: boolean;
}

/**
 * One group chip on a member row. A synced group leads with its provider's
 * logo and names the provider in a tooltip, so same-named groups from
 * different providers can be told apart without lengthening the chip. The
 * tooltip is hover-only, so the chip is also focusable (the tooltip opens on
 * keyboard focus) and carries the provider as visually hidden text for screen
 * readers.
 */
function MemberGroupChip({ group, isHighlighted }: MemberGroupChipProps) {
  const name = group.displayName || group.name;
  const color = isHighlighted ? "indigo" : "grey";

  if (!group.idpLabel) {
    return <Chip color={color}>{name}</Chip>;
  }

  return (
    <Tooltip title={group.idpLabel} asChild>
      <Chip
        className={focusableTriggerClasses}
        color={color}
        icon={
          group.idpType ? (
            <IdentityProviderLogo idpType={group.idpType} size={12} />
          ) : undefined
        }
        tabIndex={0}
      >
        <span>{name}</span>
        <VisuallyHidden>, synced from {group.idpLabel}</VisuallyHidden>
      </Chip>
    </Tooltip>
  );
}

/** A member, denormalised for a user row. */
interface Member {
  key: number;
  name?: string | null;
  email?: string | null;
  lastLogin?: string | null;
  hasApiKeys: boolean;
  groups: MemberGroup[];
}

/** A user node from the members query. The search fragment selects a superset,
 * so its nodes satisfy this too — both feed {@link memberFromUserNode}. */
type MemberNode =
  GroupedUsersGridMembersQuery$data["users"]["edges"][number]["node"];

/** Denormalises a loaded user node into a member row. */
function memberFromUserNode(node: MemberNode): Member {
  return {
    key: node.key,
    name: node.name,
    email: node.email,
    lastLogin: node.lastLogin,
    hasApiKeys: Boolean(node.apiKeys.pageInfo.total),
    groups: (node.userGroups.edges ?? [])
      .map((edge) => edge?.node)
      .filter((group): group is NonNullable<typeof group> => Boolean(group))
      .map((group) => ({
        uuid: group.uuid,
        name: group.name,
        displayName: group.displayName,
        idpType: group.source?.idpType ?? null,
        idpLabel: group.source ? idpLabelFromSource(group.source) : null,
      })),
  };
}

/** A member connection page's cursor flags. */
interface SectionPageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
  total: number | null;
}

/** What a loaded section holds: its current member page and that page's cursors. */
interface SectionData {
  members: Member[];
  pageInfo: SectionPageInfo;
}

/** A section band header row. */
interface BandRow {
  kind: "band";
  id: string;
  bucket: BucketRow;
  /** Whether the section is open (drives the disclosure caret). */
  isExpanded: boolean;
  /** Whether the open section's first member page is still loading. */
  isLoading: boolean;
}

/** A member row within a section. */
interface UserRow {
  kind: "user";
  id: string;
  bucket: BucketRow;
  member: Member;
  /** Whether this member is the signed-in user (shows a "You" badge). */
  isYou: boolean;
}

/** A section's pagination footer row. */
interface PagerRow {
  kind: "pager";
  id: string;
  bucket: BucketRow;
  pagination: StackletGridPagination;
}

/** A grid row: a section band, a user, or a section's pagination footer. */
type Row = BandRow | UserRow | PagerRow;

interface Props extends RetainedSearch {
  /** The buckets to list, one expandable section each. */
  buckets: BucketRow[];
  /** Singular noun for a bucket, e.g. "group" / "role" / "provider". */
  unit: string;
  /** Storage key for the grid's sticky column state. */
  tableId: string;
  /** Dimension search buckets matches by; omit to hide search. */
  searchKind?: "group" | "role" | "identity-provider";
  /** Whether the bucket list is fetching. */
  loading?: boolean;
  /** Toolbar content just before the Columns button — the group-by dropdown. */
  toolbarActions?: ReactNode;
  /**
   * Loads the next page of buckets when the list scrolls near the bottom; omit
   * when every bucket is loaded at once (e.g. IdP). Ignored while searching,
   * which replaces the bucket list with user-first results.
   */
  onLoadMore?: () => void;
  /**
   * Server-reported total bucket count, for the footer's loaded/total summary;
   * omit when every bucket is loaded (the loaded count is then the total).
   */
  bucketTotal?: number | null;
}

/** A search-matched section: a bucket plus the users that put it in the results. */
interface SearchSection {
  bucket: BucketRow;
  members: Member[];
}

interface MembersLoaderProps {
  /** The bucket whose members to load. */
  bucketId: string;
  /** Selects the bucket's members. */
  scope: UserBrowserScope;
  /** The current member page's cursor window. */
  page: SectionPage;
  /** Called with the loaded page. */
  onLoaded: (id: string, data: SectionData) => void;
}

/** Loads one expanded section's current member page and reports it up. */
function MembersLoader({
  bucketId,
  scope,
  page,
  onLoaded,
}: MembersLoaderProps) {
  const data = useLazyLoadQuery<GroupedUsersGridMembersQuery>(
    graphql`
      query GroupedUsersGridMembersQuery(
        $filterElement: FilterElementInput
        $first: Int
        $last: Int
        $after: String
        $before: String
      ) {
        users(
          filterElement: $filterElement
          first: $first
          last: $last
          after: $after
          before: $before
        ) {
          edges {
            node {
              apiKeys {
                pageInfo {
                  total
                }
              }
              email
              key
              lastLogin
              name
              userGroups(first: 100) {
                edges {
                  node {
                    displayName
                    name
                    source {
                      idpAlias
                      idpNamespace
                      idpType
                    }
                    uuid
                  }
                }
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
            hasPreviousPage
            startCursor
            total
          }
        }
      }
    `,
    {
      filterElement: buildFilter(scope, ""),
      first: page.first,
      last: page.last,
      after: page.after,
      before: page.before,
    },
  );

  const result = useMemo<SectionData>(
    () => ({
      members: (data.users.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map(memberFromUserNode),
      pageInfo: {
        hasNextPage: data.users.pageInfo.hasNextPage,
        hasPreviousPage: data.users.pageInfo.hasPreviousPage,
        startCursor: data.users.pageInfo.startCursor ?? null,
        endCursor: data.users.pageInfo.endCursor ?? null,
        total: data.users.pageInfo.total ?? null,
      },
    }),
    [data],
  );

  useEffect(() => {
    onLoaded(bucketId, result);
  }, [bucketId, result, onLoaded]);
  return null;
}

/** Groups a search fragment's matched users into sections by the given dimension. */
function buildSearchSections(
  edges: GroupedUsersGridSearchFragment$data["searchUsers"]["edges"],
  kind: "group" | "role" | "identity-provider",
  allBuckets: BucketRow[],
): SearchSection[] {
  const byBucket = new Map<string, SearchSection>();

  for (const edge of edges ?? []) {
    const node = edge?.node;
    if (!node) continue;

    const buckets: BucketRow[] = [];
    if (kind === "group") {
      for (const e of node.userGroups.edges ?? []) {
        const group = e?.node;
        if (!group) continue;
        const label = group.displayName || group.name;
        buckets.push({
          id: group.id,
          label,
          to: groupingDetailPath({
            kind: "group",
            uuid: group.uuid,
            name: label,
          }),
          editTo: `/settings/access-control/groups/${group.uuid}/edit`,
          memberScope: { kind: "group-uuid", groupUuid: group.uuid },
          highlightGroupUuid: group.uuid,
          detail: group.source
            ? `Synced from ${group.source.idpAlias || group.source.idpNamespace}`
            : "Local",
        });
      }
    } else if (kind === "role") {
      const seen = new Set<string>();
      for (const e of node.roleAssignments.edges ?? []) {
        const role = e?.node?.role;
        if (!role || seen.has(role.name)) continue;
        seen.add(role.name);
        buckets.push({
          id: `role:${role.name}`,
          label: role.name,
          to: null,
          memberScope: { kind: "role", roleName: role.name },
          detail: role.system ? "System role" : null,
        });
      }
    } else {
      // identity-provider: reconcile the user's linked namespaces against the
      // known provider buckets, so a search bucket is identical to its browse
      // one; a user with no matching link falls under Local Users.
      const namespaces = new Set(
        (node.identityLinks ?? [])
          .map((link) => link?.provider?.idpNamespace)
          .filter((ns): ns is string => Boolean(ns)),
      );
      const matched = allBuckets.filter(
        (bucket) =>
          bucket.memberScope.kind === "identity-provider" &&
          namespaces.has(bucket.memberScope.idpNamespace),
      );
      if (matched.length) {
        buckets.push(...matched);
      } else {
        const local = allBuckets.find(
          (bucket) => bucket.memberScope.kind === "local",
        );
        if (local) buckets.push(local);
      }
    }

    const member = memberFromUserNode(node);
    for (const bucket of buckets) {
      const existing = byBucket.get(bucket.id);
      if (existing) {
        existing.members.push(member);
      } else {
        byBucket.set(bucket.id, { bucket, members: [member] });
      }
    }
  }

  return Array.from(byBucket.values());
}

interface MemberNameCellProps {
  /** The user shown in the cell. */
  member: Member;
  /** Whether this row is the signed-in user. */
  isYou: boolean;
}

/** The avatar/name/email cell of a member row, with a "You" badge for the caller. */
function MemberNameCell({ member, isYou }: MemberNameCellProps) {
  return (
    <div className="flex h-full items-center gap-sm">
      <Avatar
        email={member.email ?? ""}
        name={member.name ?? member.email ?? ""}
        size={28}
      />
      <div className="leading-tight min-w-0">
        <div className="flex items-center gap-xs">
          <span className="truncate text-label-medium text-text-primary">
            {member.name ?? "--"}
          </span>
          {isYou ? <Chip color="blue">You</Chip> : null}
        </div>
        <div className="truncate text-label-small text-text-secondary">
          {member.email ?? "--"}
        </div>
      </div>
    </div>
  );
}

/**
 * Users grouped into expandable bands (group/role/provider). Members are rows in
 * the same grid, sharing one aligned header; each section pages on its own footer.
 */
export function GroupedUsersGrid({
  buckets,
  unit,
  tableId,
  searchKind,
  loading,
  toolbarActions,
  initialSearchTerm,
  onSearchTermChange,
  onLoadMore,
  bucketTotal,
}: Props) {
  const { currentUser } = useUserContext();

  // Read once at mount (lazy state initializer) so typing can't re-seed the
  // field or refetch.
  const [initialTerm] = useState(() =>
    searchKind ? (initialSearchTerm ?? "").trim() : "",
  );

  // Feeds the export total and the search fragment — seeded when an inherited
  // term is present, else empty until a search refetches it.
  const queryData = useLazyLoadQuery<GroupedUsersGridQuery>(
    graphql`
      query GroupedUsersGridQuery(
        $searchFilter: FilterElementInput
        $searchFirst: Int
      ) {
        ...GroupedUsersGridSearchFragment
          @arguments(filter: $searchFilter, first: $searchFirst)
        total: users(first: 1) {
          pageInfo {
            total
          }
        }
      }
    `,
    {
      searchFilter: buildFilter({ kind: "all" }, initialTerm),
      searchFirst: initialTerm ? SEARCH_LIMIT : 0,
    },
  );
  const totalUsers = queryData.total.pageInfo.total ?? 0;

  const [searchData, refetchSearch] = useRefetchableFragment<
    GroupedUsersGridSearchRefetchQuery,
    GroupedUsersGridSearchFragment$key
  >(
    graphql`
      fragment GroupedUsersGridSearchFragment on Query
      @argumentDefinitions(
        filter: { type: "FilterElementInput" }
        first: { type: "Int" }
      )
      @refetchable(queryName: "GroupedUsersGridSearchRefetchQuery") {
        searchUsers: users(filterElement: $filter, first: $first) {
          edges {
            node {
              apiKeys {
                pageInfo {
                  total
                }
              }
              email
              identityLinks {
                provider {
                  idpNamespace
                }
              }
              key
              lastLogin
              name
              userGroups(first: 100) {
                edges {
                  node {
                    displayName
                    id
                    name
                    source {
                      idpAlias
                      idpNamespace
                      idpType
                    }
                    uuid
                  }
                }
              }
              roleAssignments(first: 100) {
                edges {
                  node {
                    role {
                      name
                      system
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            total
          }
        }
      }
    `,
    queryData,
  );

  const [isSearchPending, startSearchTransition] = useTransition();

  // `search` is the live field value; the debounced apply refetches in a
  // transition so the visible rows stay put while results load.
  const [search, setSearch] = useState(initialTerm);
  const [appliedSearch, setAppliedSearch] = useState(initialTerm);
  const applySearch = useDebouncedCallback((value: string) => {
    setAppliedSearch(value);
    onSearchTermChange?.(value);
    if (!searchKind) return;
    const term = value.trim();
    startSearchTransition(() => {
      refetchSearch(
        {
          filter: buildFilter({ kind: "all" }, value),
          first: term ? SEARCH_LIMIT : 0,
        },
        { fetchPolicy: "network-only" },
      );
    });
  }, SEARCH_DEBOUNCE_MS);
  // Reflect the keystroke immediately; the apply is debounced.
  const handleSearchChange = (value: string) => {
    setSearch(value);
    applySearch.run(value);
  };
  const searching = Boolean(searchKind) && appliedSearch.trim() !== "";
  // The export carries the search filter, so the progress bar counts the
  // matches, not the whole directory. Falls back to the directory total while a
  // search refetch is still in flight, so the bar never divides by zero.
  const searchTotal = searchData.searchUsers.pageInfo.total ?? 0;
  const exportRowCount = searching && searchTotal ? searchTotal : totalUsers;

  // Filter by the applied search (not the live field) so the CSV matches the
  // rows on screen.
  const { exportInProgress, exportParams, handleExport, markComplete } =
    useFilteredCsvExport(() => buildFilter({ kind: "all" }, appliedSearch));

  const searchSections = useMemo<SearchSection[]>(
    () =>
      searching && searchKind
        ? buildSearchSections(searchData.searchUsers.edges, searchKind, buckets)
        : [],
    [searching, searchKind, searchData, buckets],
  );

  // Users per page, shared by every section and remembered per grid, so the
  // size picked in one section's footer applies to all and survives a reload.
  const [pageSize, setStoredPageSize] = useStickyPageSize(
    tableId,
    DEFAULT_PAGE_SIZE_OPTIONS,
  );

  const {
    expanded,
    pageById,
    dataById,
    loadingById,
    toggle,
    onLoaded,
    getPager,
  } = useSectionPagination<SectionData>({
    pageSize,
    onPageSizeChange: setStoredPageSize,
  });

  // A search term swaps the bucket list for user-first results — each section
  // arrives pre-populated and shown expanded.
  const currentUserEmail = currentUser.email;

  const rows: Row[] = useMemo(() => {
    if (searching) {
      return searchSections.flatMap(({ bucket, members }) => [
        {
          kind: "band" as const,
          id: `band:${bucket.id}`,
          bucket,
          isExpanded: true,
          isLoading: false,
        },
        ...members.map((member) => ({
          kind: "user" as const,
          id: `${bucket.id}:${member.key}`,
          bucket,
          member,
          isYou: Boolean(currentUserEmail) && member.email === currentUserEmail,
        })),
      ]);
    }
    return buckets.flatMap((bucket) => {
      const isExpanded = expanded.has(bucket.id);
      const section = dataById[bucket.id];
      const bandRow: BandRow = {
        kind: "band",
        id: `band:${bucket.id}`,
        bucket,
        isExpanded,
        // First page still loading — the band shows the loading bar, nothing
        // else yet.
        isLoading: isExpanded && (!section || Boolean(loadingById[bucket.id])),
      };
      if (!isExpanded || !section) return [bandRow];

      const userRows: Row[] = section.members.map((member) => ({
        kind: "user",
        id: `${bucket.id}:${member.key}`,
        bucket,
        member,
        isYou: Boolean(currentUserEmail) && member.email === currentUserEmail,
      }));

      const pager = getPager(bucket.id, {
        pageInfo: section.pageInfo,
        rowCount: section.members.length,
        unit: "user",
      });
      if (!pager) return [bandRow, ...userRows];

      return [
        bandRow,
        ...userRows,
        {
          kind: "pager" as const,
          id: `pager:${bucket.id}`,
          bucket,
          pagination: pager,
        },
      ];
    });
  }, [
    searching,
    searchSections,
    buckets,
    expanded,
    dataById,
    loadingById,
    currentUserEmail,
    getPager,
  ]);

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "disclosure",
        headerName: "",
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        sortable: false,
        // Structural, not data: hiding it would remove every expand control,
        // and with it the only way to reach an expanded section's users. So the
        // Columns picker doesn't offer it.
        hideable: false,
        align: "center",
        // Reads as part of the name column, so no divider after it.
        cellClassName: "!border-r-0",
        headerClassName: "!border-r-0",
        renderCell: ({ row }) => {
          if (row.kind !== "band") return null;
          const id = row.bucket.id;
          return (
            <DisclosureToggle
              isExpanded={row.isExpanded}
              label={row.bucket.label}
              onToggle={() => toggle(id)}
            />
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 260,
        sortable: false,
        colSpan: (params) => (params.row.kind === "user" ? 1 : COLUMN_COUNT),
        renderCell: ({ row }) => {
          if (row.kind === "band") {
            return <BandCell bucket={row.bucket} isLoading={row.isLoading} />;
          }
          if (row.kind === "pager") {
            return (
              <div className="flex h-full w-full items-center">
                <PaginationFooter {...row.pagination} />
              </div>
            );
          }
          return <MemberNameCell isYou={row.isYou} member={row.member} />;
        },
      },
      {
        field: "groups",
        headerName: "Group(s)",
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.kind !== "user") return null;
          if (row.member.groups.length === 0) return "--";
          const shown = row.member.groups.slice(0, MAX_GROUP_CHIPS);
          const hidden = row.member.groups.slice(MAX_GROUP_CHIPS);
          return (
            // content-center packs wrapped chip lines together in the middle of
            // the cell; the default (stretch) spreads them to its top and bottom.
            <div className="flex h-full flex-wrap content-center items-center gap-x-xs gap-y-xxs">
              {shown.map((group) => (
                <MemberGroupChip
                  group={group}
                  isHighlighted={group.uuid === row.bucket.highlightGroupUuid}
                  key={group.uuid}
                />
              ))}
              {hidden.length > 0 ? (
                // Hover-only tooltip, so the label is focusable (the tooltip
                // opens on keyboard focus) and names the hidden groups for
                // screen readers as visually hidden text. Always in the tab
                // order, like every other control in these grids.
                <Tooltip
                  title={
                    <ul>
                      {hidden.map((group) => (
                        <li key={group.uuid}>{memberGroupLabel(group)}</li>
                      ))}
                    </ul>
                  }
                  asChild
                >
                  <span
                    className={cn(
                      "text-label-small text-text-secondary",
                      focusableTriggerClasses,
                    )}
                    tabIndex={0}
                  >
                    <span>+{hidden.length} more</span>
                    <VisuallyHidden>
                      : {hidden.map(memberGroupLabel).join(", ")}
                    </VisuallyHidden>
                  </span>
                </Tooltip>
              ) : null}
            </div>
          );
        },
      },
      {
        field: "hasApiKeys",
        headerName: "API Keys",
        width: 76,
        maxWidth: 76,
        sortable: false,
        align: "center",
        renderCell: ({ row }) =>
          row.kind !== "user" ? null : row.member.hasApiKeys ? (
            <CheckIcon
              aria-label="Has API keys"
              className="size-4 shrink-0 text-text-positive"
            />
          ) : (
            <XIcon
              aria-label="No API keys"
              className="size-4 shrink-0 text-text-secondary"
            />
          ),
      },
      {
        field: "lastLogin",
        headerName: "Last Login",
        width: 150,
        maxWidth: 150,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === "user" ? (
            <HumanTimestamp timestamp={row.member.lastLogin} />
          ) : null,
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        sortable: false,
        align: "center",
        renderCell: ({ row }) => {
          if (row.kind !== "user") return null;
          return (
            <Link
              className="inline-flex text-text-secondary hover:text-text-primary"
              textDecoration="none"
              to={`/settings/access-control/users/${row.member.key}/edit`}
            >
              <PencilSimpleIcon
                aria-label={`Edit ${row.member.name ?? row.member.email ?? "user"}`}
                size={16}
              />
            </Link>
          );
        },
      },
    ],
    // Keep `columns` stable across expand — cells read their state off `row`,
    // not closures — or the grid re-autosizes and the columns visibly re-fit.
    [toggle],
  );

  return (
    <>
      {/* Browse-only: member loaders fill open sections. Search reads the
          refetched fragment instead. */}
      {searching
        ? null
        : [...expanded].map((id) => {
            const bucket = buckets.find((candidate) => candidate.id === id);
            if (!bucket) return null;
            return (
              <Suspense fallback={null} key={id}>
                <MembersLoader
                  bucketId={id}
                  onLoaded={onLoaded}
                  page={pageById[id] ?? firstPage(pageSize)}
                  scope={bucket.memberScope}
                />
              </Suspense>
            );
          })}

      {/* The grid block below sits flush against whatever precedes it, so the
          progress bar has to space itself off it. `empty:` drops the margin
          again while no export is running, when ExportCsv renders nothing. */}
      <div className="mb-sm empty:mb-0">
        <ExportCsv
          columns={CSV_COLUMNS}
          exportInProgress={exportInProgress}
          field="users"
          markComplete={markComplete}
          nodeId={null}
          params={exportParams}
          rowCount={exportRowCount}
        />
      </div>

      <StackletGrid
        // A small autoHeightMax keeps the grid in its own scroll viewport so
        // onRowsScrollEnd fires to load more buckets (like the other scroll grids).
        autoHeightMax={10}
        className={GRID_CLASS_NAMES}
        columnHeaderHeight={36}
        columns={columns}
        defaultDensity="comfortable"
        exportInProgress={exportInProgress}
        fixedWidthFields={FIXED_WIDTH_FIELDS}
        getRowClassName={(params) => {
          if (params.row.kind !== "band") return "";
          // White while collapsed, shaded once open (search opens every match).
          return searching || expanded.has(params.row.bucket.id)
            ? "bg-background-secondary"
            : "bg-background-primary";
        }}
        getRowId={(row) => row.id}
        handleExport={handleExport}
        // Force the column order so a stale persisted order (from before the
        // disclosure column existed) can't push the toggle to the end.
        initialState={{
          columns: {
            orderedFields: [
              "disclosure",
              "name",
              "groups",
              "hasApiKeys",
              "lastLogin",
              "actions",
            ],
          },
        }}
        loading={loading || isSearchPending}
        onRowsScrollEnd={searching ? undefined : onLoadMore}
        onSearchChange={searchKind ? handleSearchChange : undefined}
        rowHeight={52}
        rows={rows}
        search={search}
        searchAriaLabel="Search users"
        searchPlaceholder="Search users"
        slotProps={{ toolbar: { className: "py-md" } }}
        slots={{
          noRowsOverlay: () => (
            <div className="flex h-full items-center justify-center text-label-small text-text-secondary">
              {searching ? "No users match your search" : `No ${unit}s`}
            </div>
          ),
        }}
        tableId={tableId}
        toolbarActions={toolbarActions}
        exportTrailing
        hideDensityButton
        showCellVerticalBorder
        showColumnVerticalBorder
        transparentSearch
      />

      {/* Loaded/total bucket count — the grid's own row-count footer can't
          stand in, since the accordion's rows aren't buckets. Hidden while
          searching, which shows user-first matches instead. */}
      {searching || buckets.length === 0 ? null : (
        <div className="flex justify-end px-md py-sm text-label-small text-text-secondary">
          {`${buckets.length}/${bucketTotal ?? buckets.length} ${pluralize(unit, bucketTotal ?? buckets.length)}`}
        </div>
      )}
    </>
  );
}

interface DisclosureToggleProps {
  /** Whether the section is expanded. */
  isExpanded: boolean;
  /** The section name, for the toggle's accessible label. */
  label: string;
  /** Toggles the section open/closed. */
  onToggle: () => void;
}

/** The caret button in the leading disclosure column of a band row. */
function DisclosureToggle({
  isExpanded,
  label,
  onToggle,
}: DisclosureToggleProps) {
  return (
    <Button
      IconLeft={
        <CaretRightIcon
          className={isExpanded ? "rotate-90" : undefined}
          size={16}
        />
      }
      aria-expanded={isExpanded}
      aria-label={`${isExpanded ? "Collapse" : "Expand"} ${label}`}
      className="text-text-secondary hover:text-text-primary"
      onPress={onToggle}
      type="button"
      variant="plain"
    />
  );
}

interface BandCellProps {
  /** The bucket the band represents. */
  bucket: BucketRow;
  /** Whether the section's members are loading (shows a thin bar at the base). */
  isLoading: boolean;
}

/** A section band: name (linked), secondary line, and row actions. The
 * disclosure caret lives in its own leading column, not here. */
function BandCell({ bucket, isLoading }: BandCellProps) {
  return (
    <div className="relative flex h-full w-full items-center justify-between">
      {isLoading ? (
        <div className="absolute inset-x-0 bottom-0">
          <ProgressBar size="slim" indeterminate />
        </div>
      ) : null}
      <div className="leading-tight min-w-0">
        {bucket.to ? (
          <TableLink name={bucket.label} to={bucket.to} />
        ) : (
          <span className="text-label-medium-strong text-text-primary">
            {bucket.label}
          </span>
        )}
        {bucket.detail ? (
          <div className="truncate text-label-small text-text-secondary">
            {bucket.detail}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-xl pr-xs">
        {bucket.editTo ? (
          <Link
            className="inline-flex text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={bucket.editTo}
          >
            <PencilSimpleIcon aria-label={`Edit ${bucket.label}`} size={16} />
          </Link>
        ) : null}
        {bucket.to ? (
          <Link
            className="inline-flex text-text-secondary hover:text-text-primary"
            textDecoration="none"
            to={bucket.to}
          >
            <ArrowRightIcon aria-label={`View ${bucket.label}`} size={16} />
          </Link>
        ) : null}
      </div>
    </div>
  );
}
